import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { Box } from '@mui/material';

import { TextInput } from 'src/components/reusable-components/text-input/TextInput';
import {
  buildZendeskSignInUrl,
  validateAssistEmail,
} from 'src/utils/assistAuthUtils';
import { Routes } from 'src/utils/enums';
import {
  getStoredZendeskSubdomain,
  setStoredZendeskSubdomain,
} from 'src/utils/zendeskSubdomainStorage';

const ValidateEmailForm: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { t } = useTranslation('authentication-wall');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isZendeskMode = searchParams.get('zd') === 'true';
  const [email, setEmail] = useState(searchParams.get('email') ?? '');
  const [subdomain, setSubdomain] = useState(
    searchParams.get('subdomain') ?? getStoredZendeskSubdomain(),
  );
  // True once the lookup has come back 404 without a subdomain — we then
  // treat that as "this might be a ZD-provisioned tenant we don't know
  // about yet" and require the subdomain on the next submit so the recall
  // can match a ZD org via get_org_by_zendesk_subdomain.
  const [needsSubdomain, setNeedsSubdomain] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const subdomainVisible = isZendeskMode || needsSubdomain;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const trimmedSubdomain = subdomain.trim();

    if (trimmedSubdomain) {
      setStoredZendeskSubdomain(trimmedSubdomain);
    }

    try {
      const { data, status } = await validateAssistEmail(
        email,
        trimmedSubdomain || undefined,
      );

      if (data.is_zendesk_provisioned && data.subdomain) {
        window.location.assign(buildZendeskSignInUrl(data.subdomain));
        return;
      }

      let agentLoginURL = data.login_url;

      /* If org exists and doesn't use single sign on, redirect to magic link */
      if (status === 201 && !agentLoginURL.endsWith('single-sign-on')) {
        agentLoginURL += Routes.MAGIC_LINK;
      }

      window.location.assign(
        `${agentLoginURL}#extensionAuth=1&username=${encodeURIComponent(
          email,
        )}`,
      );
    } catch (error: any) {
      if (error.response?.status === 404) {
        if (!trimmedSubdomain) {
          setNeedsSubdomain(true);
        } else {
          navigate(Routes.ORG_NOT_FOUND);
        }
      } else {
        console.error(error);
      }
      setSubmitting(false);
    }
  };

  const isDisabled =
    !email.length || submitting || (needsSubdomain && !subdomain.trim());

  return (
    <>
      <Box
        component='h1'
        sx={{
          color: 'var(--color-text-primary)',
          fontSize: 'var(--font-size-2xl)',
          fontWeight: 'var(--font-weight-semibold)',
          m: 0,
        }}
      >
        {t('validateEmailForm.title')}
      </Box>
      <Box
        component='p'
        sx={{
          color: 'var(--color-text-secondary)',
          fontSize: 'var(--font-size-ms)',
          lineHeight: '21px',
        }}
      >
        {t('validateEmailForm.body')}
      </Box>
      <Box
        component='p'
        sx={{
          color: 'var(--color-slate-300)',
          fontSize: 'var(--font-size-base)',
          fontWeight: 'var(--font-weight-regular)',
          mt: '8px',
        }}
      >
        {t('validateEmailForm.joinTeamPrompt')}
      </Box>
      <Box
        component='form'
        onSubmit={handleSubmit}
        sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
      >
        <TextInput
          isRequired={false}
          key='email'
          name='email'
          onChange={e => setEmail(e.currentTarget.value)}
          placeholder={t('common.emailAddress')}
          type='email'
          value={email}
        />
        {subdomainVisible && (
          <TextInput
            isRequired={needsSubdomain}
            key='subdomain'
            name='subdomain'
            onChange={e => setSubdomain(e.currentTarget.value)}
            placeholder={
              needsSubdomain
                ? t('common.zendeskSubdomain')
                : t('common.subdomainOptional')
            }
            type='text'
            value={subdomain}
          />
        )}
        <Box
          component='button'
          disabled={isDisabled}
          sx={{
            '&:disabled': {
              background: 'var(--mui-palette-primary-100)',
              color: '#81b3b9',
              cursor: 'not-allowed',
            },
            background: 'var(--mui-palette-primary-main)',
            border: 0,
            borderRadius: 'var(--border-radius-button)',
            color: 'var(--color-solid-white)',
            cursor: 'pointer',
            fontSize: 'var(--font-size-s)',
            fontWeight: 'var(--font-weight-semibold)',
            height: '50px',
            textTransform: 'capitalize',
            userSelect: 'none',
            width: '100%',
          }}
          type='submit'
        >
          {t('validateEmailForm.continue')}
        </Box>
      </Box>
    </>
  );
};

export default ValidateEmailForm;
